/**
 * ConfirmModal — a Bootstrap confirm dialog for admin screens (replaces window.confirm).
 * English-only (admin). Renders nothing when `open` is false.
 */

import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Button colour of the confirm action. */
  variant?: 'primary' | 'danger' | 'warning';
  busy?: boolean;
  /** Disable the confirm button (e.g. until a required choice is made). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmModal: React.FC<Props> = ({
  open, title, children, confirmLabel = 'Confirm', cancelLabel = 'Cancel', variant = 'primary', busy = false, confirmDisabled = false, onConfirm, onCancel,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="confirm-modal-title">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="confirm-modal-title">{title}</h5>
              <button type="button" className="btn-close" aria-label="Close" disabled={busy} onClick={onCancel} />
            </div>
            <div className="modal-body">{children}</div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={onCancel}>{cancelLabel}</button>
              <button type="button" className={`btn btn-${variant} d-inline-flex align-items-center gap-1`} disabled={busy || confirmDisabled} onClick={onConfirm}>
                {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />} {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

export default ConfirmModal;
